import { useMemo } from 'react';

import type { Account, AccountNumber } from '../../../../generated-types';
import useTranslation from '../../../../hooks/useTranslation';
import { isValidAccountTransferTo } from '../../../../services/accountFilters';
import { TransferState } from '../form/state';

interface UnknownAccount extends Account {
    unknon: boolean;
}

const mapUnknownAccount = (accountNumber: AccountNumber, unknownAccountName: string): UnknownAccount => ({
    accountId: accountNumber,
    accountNumber,
    balance: {
        accountingBalance: 0,
        availableBalance: 0,
        creditLimit: 0,
    },
    alias: unknownAccountName,
    bank: { bankregnr: { value: 'Ukjent bank' } },
    coreProduct: '',
    currency: 'NOK',
    owners: [],
    rights: { transferFrom: false, transferTo: true, paymentFrom: false },
    status: 'UNKNOWN',
    type: 'UNKNOWN',
    unknon: true,
    additionalInfo: { authorizedcustomers: false, hasWithdrawalsCondition: false },
});

export const isUnknownAccount = (account?: Account): boolean => {
    return !!account && (account as UnknownAccount)?.unknon === true;
};
const isAccountUnknown = (accounts?: Account[], accountNumber?: AccountNumber): boolean => {
    return (
        !!accountNumber &&
        !!accounts &&
        !accounts.some((account) => account.accountNumber.value === accountNumber?.value)
    );
};
export const getToAccount = (
    state: TransferState,
    targetToAccount: Account | undefined,
    accounts: Account[] | undefined,
    unknownAccountName = 'Ukjent konto',
): Account | undefined => {
    return !targetToAccount && state.toAccount && isAccountUnknown(accounts, state.toAccount)
        ? mapUnknownAccount(state.toAccount, unknownAccountName)
        : targetToAccount;
};

const useToAccount = (
    state: TransferState,
    targetToAccount: Account | undefined,
    accounts: Account[] | undefined,
): { toAccounts: Account[]; selectedToAccount: Account | undefined; isUnknownAccount: boolean } => {
    const translated = useTranslation();

    return useMemo(() => {
        const toAccount = getToAccount(state, targetToAccount, accounts, translated.unknownAccount);
        const unKnownAccount = isUnknownAccount(toAccount);
        const toAccounts = unKnownAccount ? (accounts ?? []).concat(toAccount ?? []) : accounts;
        return {
            toAccounts: toAccounts?.filter(isValidAccountTransferTo) ?? [],
            selectedToAccount: toAccount,
            isUnknownAccount: unKnownAccount,
        };
    }, [state, accounts, translated.unknownAccount, targetToAccount]);
};

export default useToAccount;
